// MainNavMobile.utils.tsx
import React from 'react';
import Link from 'next/link';
import { CmsImage, Icon } from 'common-ui';
import NavItem from '../../../GlobalFooter/NavItem';
import type { ImageEntryType, LinkEntryType, NavigationEntryType, SiteEntryType } from 'tsconfig/types';

export function renderSocialLinks(socialLinks?: LinkEntryType[]): React.JSX.Element | null {
  if (!socialLinks?.length) {
    return null;
  }

  return (
    <div className="mainnav-mobile-drawer-social-panel">
      {socialLinks.map(({ fields }) => (
        <div key={`social-${fields?.title}`}>
          <Link style={{ borderBottom: 'none' }} prefetch={false} href={fields?.url ?? ''} data-testid={fields?.title}>
            <Icon kind={fields?.title} size="m" data-test={fields?.title} />
          </Link>
        </div>
      ))}
    </div>
  );
}

export function renderFooterNav(footerNavBar?: SiteEntryType['fields']['footerNavBar']): React.JSX.Element | null {
  if (!footerNavBar?.length) {
    return null;
  }

  return (
    <div className="mainnav-mobile-drawer-footer-links-panel">
      <div>
        {(footerNavBar as NavigationEntryType[])?.map(({ fields }) => (
          <NavItem key={`footer-${fields.title}`} fields={fields} />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders a panel of badge images, each optionally wrapped in a link.
 *
 * @param {LinkEntryType[]} [badgeImages] - An optional array of badge image entries.
 * Each entry contains fields for an icon and a URL. If no badge images are provided
 * or the array is empty, the function returns `null`.
 *
 * @returns {JSX.Element | null} A JSX element containing the rendered badge images
 * or `null` if no badge images are provided.
 */
export function renderBadges(badgeImages?: LinkEntryType[]): React.JSX.Element | null {
  if (!badgeImages?.length) {
    return null;
  }

  return (
    <div className="mainnav-mobile-drawer-badges-panel">
      {badgeImages.map(({ fields }) => {
        const iconImage = fields?.icon && (
          <CmsImage
            isNextImg
            contentItem={fields?.icon as ImageEntryType}
            disableAutoSize
            key={`img-${fields.icon.sys?.id}`}
          />
        );

        if (fields?.url) {
          return (
            <div data-testid="badge-link" key={`badge-${fields.url}`}>
              <Link prefetch={false} href={fields.url ?? ''}>
                {iconImage}
              </Link>
            </div>
          );
        }

        return iconImage;
      })}
    </div>
  );
}
